import {useEffect, useState}        from "react";
import {InputGroup, Form, Row, Col} from 'react-bootstrap';
import {useUser}                    from "../../context/UserProvider/UserProvider";
import UiConfig                     from "../../common/ui-config";

interface DataSettings {
    include_unsafe_all: boolean;
    include_unsafe_disturbing: boolean;
    include_unsafe_politically_incorrect: boolean;
    include_unsafe_sexual: boolean;
    updated_by_user_version: boolean;
    allow_search_history_google_analytics: boolean;

}


const DataSettingsForm = ({disabled}: { disabled?: boolean }) => {
    const {userAttributes, updateUserAttributes} = useUser();

    const initialSettings = userAttributes?.["custom:app_settings"] || UiConfig.accountTypeParams["free"];

    const [settings, setSettings] = useState<DataSettings>(initialSettings);

    const [loadingSettings, setLoadingSettings] = useState<Record<keyof DataSettings, boolean>>({
                                                                                                    include_unsafe_all                   : false,
                                                                                                    include_unsafe_disturbing            : false,
                                                                                                    include_unsafe_politically_incorrect : false,
                                                                                                    include_unsafe_sexual                : false,
                                                                                                    updated_by_user_version              : false,
                                                                                                    allow_search_history_google_analytics: false,
                                                                                                });

    const toggleSetting = (key: keyof DataSettings, value: boolean) => {
        if (disabled) return;

        setSettings((prevSettings) => {
            const updatedSettings = {
                ...prevSettings,
                [key]: value,
            };

            // Update `include_unsafe_all` if this checkbox is not 'include_unsafe_all' itself
            if (key !== "include_unsafe_all") {
                const allChecked = [
                    "include_unsafe_disturbing", "include_unsafe_politically_incorrect", "include_unsafe_sexual"
                ]
                    .every((individualKey) =>
                               individualKey === key ? value : prevSettings[individualKey as keyof DataSettings]
                    );

                if (allChecked) {
                    updatedSettings.include_unsafe_all = true;
                }
                else {
                    const anyUnchecked = [
                        "include_unsafe_disturbing", "include_unsafe_politically_incorrect", "include_unsafe_sexual"
                    ]
                        .some((individualKey) =>
                                  individualKey === key ? !value : !prevSettings[individualKey as keyof DataSettings]
                        );

                    if (anyUnchecked) {
                        updatedSettings.include_unsafe_all = false;
                    }
                }
            }

            return updatedSettings;
        });

    };

    const toggleToggleAll = (value: boolean) => {
        if (disabled) return;
// Identify which specific checkboxes are changing
        const changingKeys = (
            [
                "include_unsafe_all",
                "include_unsafe_disturbing",
                "include_unsafe_politically_incorrect",
                "include_unsafe_sexual",
            ] as const
        ).filter((key) => settings[key] !== value) as (keyof DataSettings)[];
// Set spinners only for the changing checkboxes
        setLoadingSettings((prev) => {
            const updatedLoadingSettings = {...prev};
            changingKeys.forEach((key) => {
                updatedLoadingSettings[key] = true;
            });
            return updatedLoadingSettings;
        });

// Update only the settings that are changing
        const updatedSettings = changingKeys.reduce(
            (acc, key) => (
                {
                    ...acc,
                    [key]: value,
                }
            ),
            {updated_by_user_version: true} // Ensure required flags are included
        );

        setSettings((prevSettings) => (
            {
                ...prevSettings,
                ...updatedSettings,
            }
        ));
    };

    const toggleAllowSearchHistory = (value: boolean) => {
        setLoadingSettings((prev) => (
            {
                ...prev,
                allow_search_history_google_analytics: true,
            }
        ));

        toggleSetting("allow_search_history_google_analytics", value);
    };

    useEffect(() => {
        const keys = Object.keys(loadingSettings).filter(
            (key) => loadingSettings[key as keyof DataSettings]
        ) as (keyof DataSettings)[];
        if (disabled || keys.length === 0) return;

        const saveSettings = async () => {
            try {
                const newSettings = {
                    "custom:app_settings": settings,
                };

                for (const key of keys) {
                    await updateUserAttributes(newSettings);

                    setLoadingSettings((prev) => (
                        {
                            ...prev,
                            [key]: false,
                        }
                    ));
                }
            }
            catch (error) {
                console.error(`Failed to update settings:`, error);

                setLoadingSettings((prev) => {
                    const updatedState = {...prev};
                    keys.forEach((key) => (
                        updatedState[key] = false
                    ));
                    return updatedState;
                });
            }
        };

        saveSettings();
    }, [settings]);

    return (
        <div className="account-settings-form">
            <h2>Data Settings</h2>
            <Form>
                <div className="custom-form-check">
                    <div className="d-flex align-items-center">
                        <Form.Check
                            disabled={disabled || loadingSettings.include_unsafe_all}
                            type="checkbox"
                            label="Include all unsafe statements"
                            checked={settings.include_unsafe_all}
                            onChange={(e) => toggleToggleAll(e.target.checked)}
                        />
                        {loadingSettings.include_unsafe_all && (
                            <div className="spinner-border spinner-border-sm" role="status"
                                 aria-hidden="true"></div>
                        )}
                    </div>
                    <Form.Text className="text-muted custom-form-text mb-3">
                        Enable or disable all unsafe statement types at once.
                    </Form.Text>
                </div>
                {/* Individual Checkboxes with Explicitly Typed Keys */}
                {[
                    {
                        key        : "include_unsafe_disturbing" as const,
                        label      : "Include disturbing content",
                        description: "Permit potentially distressing material.",
                    },
                    {
                        key        : "include_unsafe_politically_incorrect" as const,
                        label      : "Include politically incorrect content",
                        description: "Allow content that may not align with politically correct norms."
                    },
                    {
                        key        : "include_unsafe_sexual" as const,
                        label      : "Include sexual content",
                        description: "Enable inclusion of sexual material.",
                    },
                ].map(({key, label, description}) => (
                    <div className="custom-form-check ps-5" key={'data_setting' + key}>
                        <div className="d-flex align-items-center">
                            <Form.Check
                                disabled={disabled || loadingSettings[key]}
                                type="checkbox"
                                label={label}
                                checked={settings[key]}
                                onChange={(e) => {
                                    setLoadingSettings((prev) => (
                                        {
                                            ...prev,
                                            [key]: true,
                                        }
                                    ));
                                    toggleSetting(key, e.target.checked);
                                }}
                            />
                            {loadingSettings[key] && (
                                <div
                                    className="spinner-border spinner-border-sm ms-2"
                                    role="status"
                                    aria-hidden="true"
                                ></div>
                            )}
                        </div>
                        <Form.Text className="text-muted custom-form-text mb-3">
                            {description}
                        </Form.Text>
                    </div>

                ))}
            </Form>

            {/* Privacy Settings */}
            <h2>Privacy Settings</h2>
            <Form>
                <div className="custom-form-check">
                    <div className="">
                        <Form.Check
                            type="checkbox"
                            disabled={disabled || loadingSettings.allow_search_history_google_analytics}
                            label="Allow PersonalityMap to track my search history"
                            checked={settings.allow_search_history_google_analytics}
                            onChange={(e) => toggleAllowSearchHistory(e.target.checked)}
                        />
                        {loadingSettings.allow_search_history_google_analytics && (
                            <div className="spinner-border spinner-border-sm ms-2" role="status"
                                 aria-hidden="true"></div>
                        )}
                        <Form.Text className="text-muted custom-form-text mb-3">
                            We track how you interact with the site to improve our
                            service and make it more useful for you.
                            <br/>
                            For analytics purposes, <strong>all user data is anonymized</strong>. We do not track your
                            search
                            history in any other manner.
                        </Form.Text></div>
                </div>
            </Form>
        </div>
    );
};
export default DataSettingsForm;

